<template>
  <div class="edit-program-runtimes g-edit-component">
    <div class="g-two-columns">
      <div class="g-two-columns__column">
        <h3 class="edit-program-runtimes__info-text">
          <slot v-if="selectedTimesArrayItemId === null">
            {{ $t('messages.programEdit.setStartTimes.irrigationData') }}
          </slot>
          <slot v-else>
            {{ $t('messages.programEdit.setStartTimes.editingTime') }} {{ timesArray.findIndex(item => item.id === selectedTimesArrayItemId) + 1 }}
          </slot>
        </h3>

        <ol class="edit-program-runtimes__settings">
          <li class="edit-program-runtimes__settings__item">
            <p>{{ $t('messages.sharedTexts.time.startTime') }}:</p>
            <span
              v-if="falseStartTime === true"
              class="edit-program-runtimes__settings__warning"
            >
              {{ $t('messages.programEdit.setStartTimes.duplicatesNotAllowed') }}
            </span>
            <vue-timepicker
              v-model="currentConfiguration.timePicker.data"
              :format="currentConfiguration.timePicker.format"
              @change="timePickerChange"
            />
          </li>
          <li class="edit-program-runtimes__settings__item">
            <p>
              {{ computedRuntimeTitle }}
            </p>
            <input
              disabled
              class="edit-program-runtimes__settings__item--runtime"
              :value="computedRuntime.timeFormatted"
            >
          </li>
          <li
            v-if="ModalData.capabilities.steps.maxStationsPerStep === 1"
            class="edit-program-runtimes__settings__item"
          >
            <p>{{ $t('messages.sharedTexts.cycles') }}</p>
            <input
              v-model="currentConfiguration.cycles"
              class="edit-program-runtimes__settings__item--cycles"
              type="number"
              @change="evaluateCycles(currentConfiguration)"
            >
          </li>
          <li
            v-if="ModalData.capabilities.steps.maxStationsPerStep === 1"
            class="edit-program-runtimes__settings__item"
            :title="(currentConfiguration.cycles <= 1) ? $t('messages.programEdit.setStartTimes.onlyMultipleCycles') : ''"
          >
            <p>
              {{ $t('messages.sharedTexts.soakTime') }}
              <small v-if="currentConfiguration.cycles < 2">
                {{ $t('messages.programEdit.setStartTimes.onlyMultipleCycles') }}
              </small>
            </p>
            <timepicker-element
              v-bind="{Data: currentConfiguration.soakTime}"
              :class="{disabled: currentConfiguration.cycles <= 1}"
            />
          </li>
        </ol>

        <div class="edit-program-runtimes__button-wrapper">
          <button
            v-if="(selectedTimesArrayItemId !== null)"
            class="g-btn g-btn--rounded edit-program-runtimes__button edit-program-runtimes__button--save g-btn g-btn--blue"
            type="button"
            name="button"
            @click="saveClick"
          >
            <font-awesome-icon :icon="['fas', 'save']" /><span> {{ $t('messages.sharedTexts.controls.update') }}</span>
          </button>
          <button
            v-if="showCreateButton"
            class="g-btn g-btn--rounded edit-program-runtimes__button edit-program-runtimes__button--create g-btn g-btn--blue"
            type="button"
            name="button"
            @click="createClick"
          >
            <font-awesome-icon :icon="['fas', 'plus']" /><span> {{ $t('messages.sharedTexts.controls.create') }}</span>
          </button>
          <button
            v-if="selectedTimesArrayItemId !== null"
            class="g-btn g-btn--rounded edit-program-runtimes__button edit-program-runtimes__button--delete g-btn g-btn--blue"
            type="button"
            name="button"
            @click="deleteClick"
          >
            <font-awesome-icon :icon="['fas', 'trash']" /><span> {{ $t('messages.sharedTexts.controls.delete') }}</span>
          </button>
        </div>
      </div>
      <div class="g-two-columns__column">
        <div class="edit-program-runtimes__times">
          <table>
            <tr>
              <td>{{ $t('messages.programEdit.setStartTimes.entryNr') }}</td>
              <td>{{ $t('messages.sharedTexts.controls.start') }}</td>
              <td v-if="ModalData.capabilities.steps.maxStationsPerStep === 1">
                {{ $t('messages.sharedTexts.cycles') }}
              </td>
              <td v-if="ModalData.capabilities.steps.maxStationsPerStep === 1">
                {{ $t('messages.sharedTexts.soakTime') }}
              </td>
              <td>{{ $t('messages.sharedTexts.irrigationTime') }}</td>
              <td v-if="ModalData.capabilities.steps.maxStationsPerStep === 1">
                {{ $t('messages.sharedTexts.totalRuntime') }}
              </td>
              <td>{{ $t('messages.sharedTexts.controls.end') }}</td>
            </tr>
            
            <tr
              v-for="(time, index) in computedTimesArray"
              :key="index"
              class="edit-program-runtimes__times__time"
              :class="{'edit-program-runtimes__times__time--active': time.id === selectedTimesArrayItemId}"
              @click="setCurrentTimeConfiguration(time.id)"
            >
              <td>{{ index + 1 }}</td>
              <td class="nowrap">
                {{ _secondsToClock(time.timePicker.time) }}
              </td>
              <td v-if="ModalData.capabilities.steps.maxStationsPerStep === 1">
                {{ time.cycles }}
              </td>
              <td v-if="ModalData.capabilities.steps.maxStationsPerStep === 1">
                {{ _secondsToFormat(time.soakTime.time) !== '' ? _secondsToFormat(time.soakTime.time) : '0' }}
              </td>
              <td v-if="ModalData.capabilities.steps.maxStationsPerStep === 1">
                {{ _secondsToFormat(_getRuntimeFromObj(time) - time.soakTime.time * (time.cycles - 1)) }}
              </td>
              <td>
                {{ _secondsToFormat(_getRuntimeFromObj(time)) }}
              </td>
              <td class="nowrap">
                {{ _getEndTimeFormatted(time).offsetTime }}
                <slot v-if="_getEndTimeFormatted(time).offsetDays">
                  +{{ _getEndTimeFormatted(time).offsetDays }}{{ $tc('messages.sharedTexts.time.d', 1) }}
                </slot>
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {SocketHandler, LifeCycle} from '@/assets/javascript/mixins/Mixins'
import {secondsToObj, timeToImperialObj, imperialToTimeObj} from '@/assets/javascript/Time-lib'
import VueTimepicker from 'vue2-timepicker'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import TimepickerElement from '@/components/elements/TimepickerElement.vue'
import moment from 'moment'
import _ from "lodash"

export default {
  name: 'EditProgramStartTimes',
  components: {
    'font-awesome-icon': FontAwesomeIcon,
    'vue-timepicker': VueTimepicker,
    'timepicker-element': TimepickerElement
  },
  mixins: [SocketHandler, LifeCycle],
  props: {
    SocketMessage: {
      type: Object,
      default: function() {
        return{}
      }
    },
    PageMessage: {
      type: Object,
      default: function() {
        return{}
      }
    },
    ModalData: {
      type: Object,
      default: function() {
        return{}
      }
    },
    store: {
      type: Object,
      default: function() {
        return{}
      }
    }
  },
  data() {
    return {
      selectedTimesArrayItemId: null,
      timesArray: [],
      currentConfiguration: {},
      falseStartTime: false
    }
  },
  computed: {
    computedTimePicker() {
      return this.currentConfiguration.timePicker
    },
    computedTimesArray() {
      return this.timesArray.slice().sort((a, b) => {
        return a.timePicker.time - b.timePicker.time
      })
    },
    computedRuntime() {
      let time = 0
      this.ModalData.steps.forEach((step) => {
        let stepTime = Math.floor((parseInt(step.runtime, 10) + 9) / 10) * 10
        let prgNr = this.ModalData.ID -1
        let waterBudgetPercent = this.store.data.programs[prgNr].waterBudget
        let wbStepTime = stepTime / 100 * waterBudgetPercent
        if(this.ModalData.capabilities.steps.maxStationsPerStep === 1) {
          time += wbStepTime * step.decoders.length
        } else {
          time += wbStepTime
        }
      })

      if( !isNaN(time) && time !== 0) {
        const timeFormatted = this._secondsToFormat(time)
        return {
          time: time,
          timeFormatted
        }
      } else {
        return{
          time: 0,
          timeFormatted: '0'
        }
      }
    },
    computedRuntimeTitle(){
      const plural = (this.ModalData.steps.length > 1) ? 0: 1
      if(this.ModalData.capabilities.steps.maxStationsPerStep === 1) {
        return this.$t('messages.programEdit.setStartTimes.runtimePerCycles', {
          length: this.ModalData.steps.length,
          station: this.$tc('messages.sharedTexts.stations.station', plural)
        })
      } else {
        return this.$t('messages.programEdit.setStartTimes.runtimeForCycles', {
          length: this.ModalData.steps.length,
          step: this.$tc('messages.sharedTexts.step', plural)
        })
      }
    },
    computedSettingsUse12HourFormat() {
      return this.store.getData('settings.time.use12HourClock')
    },
    showCreateButton() {
      let show = false

      if(this.timesArray.length < this.ModalData.capabilities.startTimes.maxStarts) {
        if(this.timesArray.length === 0) {
          show = true
        }
        if(this.selectedTimesArrayItemId === null && this.falseStartTime === false) {
          show = true
        }
      }

      return show
    }
  },
  watch: {
    computedTimePicker: {
      handler(val) {
        this.$nextTick(() => {
          const indexMatch = this.timesArray.find((element) => element.timePicker.time === val.time)
          this.falseStartTime = indexMatch !== undefined ? indexMatch.id !== this.selectedTimesArrayItemId : false
        })
      }, deep: true
    }
  },
  created() {
    this.currentConfiguration = this._createNewRuntime()
  },
  mounted() {
    this.timesArray = this._createInitialTimesArray()
    this.SocketMessageSend({
      component: 'Programs',
      command: 'Mounted'
    })
  },
  methods: {
    _getEndTime(time) {
      return time.timePicker.time + this._getRuntimeFromObj(time)
    },
    _getEndTimeFormatted(obj) {
      const endTime = this._getEndTime(obj)
      const offsetDays = Math.floor(this._getEndTime(obj) / 86400) // 1440 * 60
      const offsetEndtime = this._getEndTime(obj) % 86400

      return {
        time: endTime,
        offsetTime: this._secondsToClock(offsetEndtime),
        offsetDays: offsetDays
      }
    },
    _secondsToClock(seconds) {
      return moment.utc(seconds * 1000).format(this.computedSettingsUse12HourFormat ? 'hh:mm a' : 'HH:mm')
    },
    _getRuntimeFromObj(obj) {
      let time = 0

      if(this.ModalData.capabilities.steps.maxStationsPerStep === 1) {
        time = this.computedRuntime.time * obj.cycles + obj.soakTime.time * (obj.cycles - 1)
      } else {
        time = this.computedRuntime.time
      }
      return time
    },
    _secondsToFormat(time) {
      const timeObj = secondsToObj(time)
      const hours = (timeObj.hh !== 0 && typeof timeObj.hh === 'number') ? timeObj.hh + this.$tc('messages.sharedTexts.time.h', 1) + ' ' : ''
      const minutes = (timeObj.mm !== 0 && typeof timeObj.mm === 'number') ? timeObj.mm + this.$tc('messages.sharedTexts.time.m', 1) + ' ' : ''
      const seconds = (timeObj.ss !== 0 && typeof timeObj.ss === 'number') ? timeObj.ss + this.$tc('messages.sharedTexts.time.s', 1) : ''
      return hours + minutes + seconds
    },
    setCurrentTimeConfiguration(index) {
      this.selectedTimesArrayItemId = index
      this.currentConfiguration = JSON.parse(JSON.stringify(this.timesArray.find(item => item.id === index)))

      let data = this._timePickerDataFromSeconds(this.currentConfiguration.timePicker.time).timeFormatted

      const hh = parseInt(data.hh, 10)
      const mm = parseInt(data.mm, 10)
      const ss = parseInt(data.ss, 10)
      data = Object.assign({}, this.currentConfiguration.timePicker.time, {
        HH: hh > 9 ? hh : '0' + hh,
        mm: mm > 9 ? mm : '0' + mm,
        ss: ss > 9 ? ss : '0' + ss,
        A: data.A
      })
      data.hh = data.HH

      this.currentConfiguration.timePicker.data = data
    },
    _createNewRuntime() {
      const format = this.computedSettingsUse12HourFormat ? 'hh:mm A' : undefined
      return {
        id: _.uniqueId(),
        timePicker: {
          format,
          data: {
            HH: '12',
            hh: '12',
            mm: '00',
            ss: '00',
            A: 'PM'
          },
          time: 43200
        },
        soakTime: {
          hh: 0,
          mm: 0,
          ss: 0,
          hhMin: 0,
          mmMin: 0,
          mmMax: 59,
          ssMin: 0,
          ssMax: 59,
          time: 0
        },
        cycles: 1
      }
    },
    evaluateCycles(configuration) {
      if(this.ModalData.capabilities.steps.maxStationsPerStep === 1) {
        const cyclesInt = parseInt(configuration.cycles, 10)
        if(cyclesInt <= 1) {
          configuration.cycles = 1
          Object.assign(this.currentConfiguration.soakTime, {
            hh: 0,
            mm: 0,
            ss: 0
          })
        }
        if(cyclesInt > 99) {
          configuration.cycles = 99
        }
      }
    },
    _timePickerDataFromSeconds(time) {
      let timeFormatted = secondsToObj(time)

      if(this.computedSettingsUse12HourFormat) {
        timeFormatted = timeToImperialObj(`${timeFormatted.hhString}:${timeFormatted.mmString}:${timeFormatted.ssString}`)
      }

      return {
        timeFormatted,
        time
      }
    },
    timePickerChange(e) { // saves time in seconds
      const data = e.data
      let time = 0
      let timeFormatted

      if(this.computedSettingsUse12HourFormat) {
        timeFormatted = `${data.hh}:${data.mm} ${data.A}`
        time = moment(timeFormatted, 'hh:mm A')
      } else {
        timeFormatted = `${data.HH}:${data.mm}`
        time = moment(timeFormatted, 'HH:mm')
      }

      time = time.diff(moment().startOf('day'), 'seconds')
      this.computedTimePicker.time = time
    },
    _send(exportData) {
      for (let i = 0; i < this.ModalData.capabilities.startTimes.maxStarts; i++) {
        const time = exportData[i]
        const msg = {
          Category: 'Irrigation',
          Type: 'IrrPgmStart',
          Oper: 'Upd', // Only using Upd
          PgmId: parseInt(this.ModalData.ID),
          Seq: parseInt(i + 1)
        }
        if(time !== undefined) {
          Object.assign(msg, {
            Time: time.starttime,
            Cycles: time.cycles,
            EndTime: time.endtime
          })
        } else {
          Object.assign(msg, {
            Time: -1,
            Cycles: null,
            EndTime: null
          })
        }
        this.SocketMessageSend(msg)
      }
    },
    createClick() {
      if(this.falseStartTime === false) {

        this.timesArray.push(JSON.parse(JSON.stringify(this.currentConfiguration))) // {...obj} doesn't seem to work
        const exportData = this._getExportData()
        this._send(exportData)
        this.selectedTimesArrayItemId = null
        this.currentConfiguration = this._createNewRuntime()
        this.$set(this.ModalData, 'starts', exportData)


        // hotfix: store data in global var to avoid being
        // overwritten by faulty socketdata sync
        window.programs[this.ModalData.ID].starts = exportData
      }
    },
    saveClick() {
      const current = JSON.parse(JSON.stringify(this.currentConfiguration)) // {...obj} doesn't seem to work
      const indexMatch = this.timesArray.find((element) => element.timePicker.time === current.timePicker.time)

      if(this.falseStartTime === false || indexMatch.id === this.selectedTimesArrayItemId) {
        const index = this.timesArray.findIndex(item => item.id === this.selectedTimesArrayItemId)
        this.$set(this.timesArray, index, current)
        const exportData = this._getExportData()
        this._send(exportData)
        this.selectedTimesArrayItemId = null
        this.currentConfiguration = this._createNewRuntime()
        this.$set(this.ModalData, 'starts', exportData)
      }
    },
    deleteClick() {
      this.timesArray.splice(this.timesArray.findIndex(item => item.id === this.selectedTimesArrayItemId), 1)
      const exportData = this._getExportData()
      this._send(exportData)
      this.selectedTimesArrayItemId = null
      this.currentConfiguration = this._createNewRuntime()
      this.$set(this.ModalData, 'starts', exportData)


      // hotfix: store data in global var to avoid being
      // overwritten by faulty socketdata sync
      window.programs[this.ModalData.ID].starts = exportData

    },
    _getExportData() {
      if(this.ModalData.capabilities.steps.maxStationsPerStep === 1) {
        return this.timesArray.map((obj) => {
          return{
            starttime: parseInt(Math.floor(obj.timePicker.time / 60)),
            cycles: parseInt(obj.cycles),
            endtime: parseInt(Math.floor(this._getEndTime(obj) / 60))
          }
        })
      } else {
        return this.timesArray.map((obj) => {
          return{
            starttime: parseInt(obj.timePicker.time / 60)
          }
        })
      }
    },
    _calculateSoakTime(obj) {
      let timeInSeconds = 0

      const endTime = parseInt(obj.endTime)
      const startTime = parseInt(obj.startTime)
      const cycles = parseInt(obj.cycles)

      if(obj.cycles > 1) {
        timeInSeconds = ((endTime - startTime) - (this.computedRuntime.time * cycles)) / (cycles - 1)
        timeInSeconds = Math.abs(timeInSeconds)
      }

      if(isNaN(timeInSeconds)) {
        console.error('_calculateSoakTime: soakTime calculation')
      }

      let time = secondsToObj(timeInSeconds)
      time.time = timeInSeconds

      return time
    },
    _createInitialTimesArray() {
      let rtn = []
      if(this.ModalData.starts && this.ModalData.starts.length) {
        rtn = this.ModalData.starts.map((time) => {
          
          let runtime = this._createNewRuntime() // Init
          let startTime = parseInt(time.starttime, 10) * 60 // time comes in minutes over midnight, we need seconds
          let endTime = parseInt(time.endtime, 10) * 60 // time comes in minutes since starttime, we need seconds
          const cycles = !isNaN(time.cycles) ? parseInt(time.cycles, 10) : 1
  
          runtime.cycles = cycles
          runtime.timePicker.time = startTime
          runtime.soakTime = Object.assign({}, runtime.soakTime, this._calculateSoakTime({
            startTime,
            endTime,
            cycles
          }))
  
          return runtime
        })
      }

      return rtn
    }
  }
}
</script>

<style lang="scss">
  .edit-program-runtimes {
    text-align: center;
    + * {
      border-top: 1px solid $color-grey;
    }

    input {
      color: $font-primary-color;
    }

    hr {
      clear: both;
      border: 0;
    }

    .timepicker-element {
      box-sizing: border-box;
      width: 100%;

      &.disabled {
        &, * {
          pointer-events: none;
          background-color: lighten($color-grey, 20);
          border-color: $color-grey;
        }
      }
    }

    .time-picker {
      display: inline-block;
      width: 100%;

      .display-time, .dropdown, .select-list {
        box-sizing: border-box;
        width: 100%;
      }

      * {
        box-sizing: border-box;
      }
    }

    &__info-text {
      color: $color-grey-dark;
      margin: 0;
    }

    &__input-title {
      clear: both;
      font-size: 15px;
      padding: 10px 0 0;
      margin: 0;
      color: $color-grey-darker;
      font-family: $font-primary;
    }

    &__select-wrapper {
      position: relative;
      display: inline-block;
    }

    &__button-wrapper {
      margin-top: 20px;
      display: flex;
      justify-content: space-between;

      > * {
        flex: 0 1 auto;
      }
    }

    &__settings {
      &__item {
        &--cycles, &--runtime {
          box-sizing: border-box;
          width: 100%;
          text-align: center;
        }

        &--runtime {
          background-color: lighten($color-grey, 20);
        }
      }
      &__warning {
        color: $color-red-error;
      }
    }

    &__button {
      width: 33%;
      margin: 10px 10px 20px;
      max-width: 110px;
      display: flex;
      align-items: center;

      > * {
        font-size: 12px;

        &.svg-inline--fa {
          font-size: 10px;
          + * {
            margin-left: 5px;
          }
        }
      }

      &--save {
        .svg-inline--fa {
          transform: translateY(1px);
          padding-left: 10px;
        }
      }

      &--delete {
        background: $color-red-error;
      }

      &--back {
        .svg-inline--fa {
          transform: translateY(1px);
          padding-right: 10px;
        }
      }
    }

    &__text {
      max-width: 70%;
    }

    &__times-day-title {
      text-align: center;
      position: absolute;
      top: 0px;
      left: 0;
      right: 0;
    }

    &__times {
      height: 100%;
      width: 100%;
      // display: flex;
      text-align: left;
      margin-top: 30px;
      flex-wrap: wrap;

      table {
        box-sizing: border-box;
        margin-left: -5px;
        width: calc(100% + 10px);
        font-size: 11px;
        text-align: center;

        tr:first-child {
          font-family: $font-primary-semibold;
          border: 1px solid $color-grey;
          font-family: $font-primary-semibold;
          background-color: lighten($color-grey, 20);
        }
        td{
          padding: 8px;
          border: 1px solid $color-grey;
        }
        tr td:first-child {
          display: none;
        }
      }

      &__time {
        transition: transform 0.2s;
        cursor: pointer;

        .nowrap {
          white-space: nowrap;
        }

        &-wrapper {
          width: 100%;
        }

        &--active {
          td {
            background: $color-blue-marine !important;
            color: $color-white;
          }
        }
      }
    }
  }
  @media screen and (min-width: $bp-mobile) {
    .edit-program-runtimes {
      table {
        tr:first-child {}
        td {}
        tr td:first-child {
          display: table-cell;
        }
      }
    }
  }

  @media screen and (min-width: $bp-desktop) {
    .edit-program-runtimes {
      text-align: left;

      .input-element {
        &__name {
          width: 100%;
        }
      }

      .g-two-columns {
        &__column {
          width: 60%;
          &:first-child {
            width: 40%;
          }
        }
      }

      &__button-wrapper {
        clear: both;
        justify-content: space-between;

        > * {
          margin: 10px 0;
        }
      }
    }
  }
</style>
